import { concat, Observable, of, timer } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import { VIA_GEN_STATUS } from '@cpd-app/shared-module/constants';
import * as GoalActions from './goal.actions';

/**
 * AI-5. What the Goals Report does when the Via status stream is gone.
 *
 * The stream (connectGenStatus$) is the only thing that moves a queued or
 * in-progress report forward on the page. When it cannot connect or drops for
 * good, connectGenStatus$ retries three times and then dispatches
 * connectGenStatusFailure, and before this nothing read the report again: the
 * backend finished it, filed it, and the member watched "Via is reading this
 * section" until they reloaded. That is what Jackson hit on 25 Sep.
 *
 * So the page falls back to re-reading the report itself. genAiGoalsReport is
 * the page's ordinary read (no forceUpdate), so a poll never starts or pays
 * for a generation.
 *
 * It stops the moment the report settles. If it never does, it gives up after
 * the backend's own stale window (REPORT_STALE_MINUTES, 10 minutes) and marks
 * the report failed, which shows the failed panel with its retry, instead of a
 * spinner with no end.
 */
export const VIA_FALLBACK_POLL_MS = 5000;

/** 120 x 5s = 10 minutes, the backend's stale-report window. */
export const VIA_FALLBACK_MAX_POLLS = 120;

/** Completed, failed, or no report at all: nothing more to wait for. */
function isSettled(status: string | null | undefined): boolean {
  return (
    !status ||
    status === VIA_GEN_STATUS.completed ||
    status === VIA_GEN_STATUS.failed
  );
}

export function viaReportFallbackPoll(
  status$: Observable<string | null | undefined>
) {
  return concat(
    timer(VIA_FALLBACK_POLL_MS, VIA_FALLBACK_POLL_MS).pipe(
      take(VIA_FALLBACK_MAX_POLLS),
      map(() => GoalActions.genAiGoalsReport())
    ),
    of(GoalActions.genStatusReportFailed())
  ).pipe(takeUntil(status$.pipe(filter(isSettled))));
}
